export type WeaponRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export const RARITY_COLORS: Record<WeaponRarity, string> = {
  common: '#b0b0b0',
  uncommon: '#30c030',
  rare: '#3399ff',
  epic: '#b455ff',
  legendary: '#ff9933',
};

export const RARITY_LABELS: Record<WeaponRarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

const RARITY_ORDER: WeaponRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const ROLL_WEIGHTS: { rarity: WeaponRarity; weight: number }[] = [
  { rarity: 'common', weight: 45 },
  { rarity: 'uncommon', weight: 28 },
  { rarity: 'rare', weight: 18 },
  { rarity: 'epic', weight: 7 },
  { rarity: 'legendary', weight: 2 },
];

/** Weighted rarity roll — common most frequent, legendary ~2%. */
export function rollRarity(): WeaponRarity {
  return rollRarityBiased('common');
}

/** Weighted roll restricted to `minRarity` and above (re-normalized weights). */
export function rollRarityBiased(minRarity: WeaponRarity = 'uncommon'): WeaponRarity {
  const minIdx = RARITY_ORDER.indexOf(minRarity);
  const pool = ROLL_WEIGHTS.filter(({ rarity }) => RARITY_ORDER.indexOf(rarity) >= minIdx);
  const total = pool.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = Math.random() * total;
  for (const { rarity, weight } of pool) {
    roll -= weight;
    if (roll <= 0) return rarity;
  }
  return pool[pool.length - 1]?.rarity ?? minRarity;
}

/** Supply drop weapon: min rare, ~50% epic, ~15% legendary. */
export function rollSupplyDropWeaponRarity(): WeaponRarity {
  const roll = Math.random();
  if (roll < 0.15) return 'legendary';
  if (roll < 0.65) return 'epic';
  return 'rare';
}

/** Per-tier damage multipliers for DPS scaling (22X phase 1). */
export const RARITY_DAMAGE_MULT: Record<WeaponRarity, number> = {
  common: 1.0,
  uncommon: 1.08,
  rare: 1.15,
  epic: 1.22,
  legendary: 1.3,
};

/** Fire-rate bonus at top tiers — pairs with damage for DPS curve. */
export function getStatMultipliers(rarity: WeaponRarity): { damageMult: number; cooldownMult: number } {
  const idx = Math.max(0, RARITY_ORDER.indexOf(rarity));
  const t = idx / (RARITY_ORDER.length - 1);
  return {
    damageMult: RARITY_DAMAGE_MULT[rarity],
    cooldownMult: 1 - t * 0.08,
  };
}

/** Numeric rank for comparing weapon tiers (higher = better). */
export function rarityRank(rarity: WeaponRarity): number {
  return RARITY_ORDER.indexOf(rarity);
}

/** Bump rarity one tier; null when already legendary. */
export function upgradeRarity(rarity: WeaponRarity): WeaponRarity | null {
  const idx = RARITY_ORDER.indexOf(rarity);
  if (idx < 0 || idx >= RARITY_ORDER.length - 1) return null;
  return RARITY_ORDER[idx + 1] ?? null;
}

/** Epic+ weapons get a rarity prefix in HUD labels. */
export function formatRarityWeaponName(rarity: WeaponRarity, baseName: string): string {
  if (rarity === 'epic' || rarity === 'legendary') {
    return `${RARITY_LABELS[rarity]} ${baseName}`;
  }
  return baseName;
}
